import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

import RentalYieldCalculator from './RentalYieldCalculator';
import HomeLoanCalculator from './HomeLoanCalculator';
import SWPCalculator from './SWPCalculator';
import InsuranceVsMFCalculator from './InsuranceVsMFCalculator';
import RentVsBuyCalculator from './RentVsBuyCalculator';

import { useFinancial } from '../context/FinancialContext';
import { calculateGoalMetrics } from '../utils/calculations';

const {
  FiCalculator, FiHome, FiDollarSign, FiShield, FiTrendingUp, FiPieChart
} = FiIcons;

const calculatorCards = [
  {
    id: 'rent-vs-buy',
    title: 'Rent vs Buy Time Value',
    description: 'Compare buying a flat today vs renting and investing the difference',
    icon: FiHome,
    color: 'blue',
    features: ['EMI vs SIP', 'Property Appreciation', 'Opportunity Cost']
  },
  {
    id: 'insurance-vs-mf',
    title: 'Insurance vs Mutual Fund',
    description: 'Compare returns between insurance policies and mutual fund investments',
    icon: FiShield,
    color: 'purple',
    features: ['IRR Calculation', 'Tax Impact Analysis', 'Year-wise Breakdown']
  },
  {
    id: 'swp',
    title: 'SWP Calculator',
    description: 'Plan systematic withdrawal for future income needs',
    icon: FiDollarSign,
    color: 'green',
    features: ['Future Income Planning', 'Corpus Calculation', 'Timeline Analysis']
  },
  {
    id: 'home-loan',
    title: 'Home Loan Calculator',
    description: 'Calculate EMI, prepayment strategies, and SIP alternatives',
    icon: FiHome,
    color: 'orange',
    features: ['EMI Calculation', 'Prepayment Strategy', 'SIP vs EMI']
  },
  {
    id: 'rental-yield',
    title: 'Rental Yield Calculator',
    description: 'Compare property investment returns vs lumpsum alternatives',
    icon: FiTrendingUp,
    color: 'blue',
    features: ['Rental Yield Analysis', 'Property vs Lumpsum', 'Tax Comparison']
  }
];

function Calculators() {
  const { state, dispatch } = useFinancial();
  const [activeCalculator, setActiveCalculator] = useState(null);

  const handleAddHomeLoanGoal = (goalData) => {
    const metrics = calculateGoalMetrics(goalData, state.userProfile);
    const goalWithMetrics = { ...goalData, ...metrics };
    dispatch({ type: 'ADD_GOAL', payload: goalWithMetrics });
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        icon: 'text-blue-600',
        iconBg: 'bg-blue-100',
        hover: 'hover:border-blue-400 hover:shadow-blue-100'
      },
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        icon: 'text-green-600',
        iconBg: 'bg-green-100',
        hover: 'hover:border-green-400 hover:shadow-green-100'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        icon: 'text-orange-600',
        iconBg: 'bg-orange-100',
        hover: 'hover:border-orange-400 hover:shadow-orange-100'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        icon: 'text-purple-600',
        iconBg: 'bg-purple-100',
        hover: 'hover:border-purple-400 hover:shadow-purple-100'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-7xl mx-auto"
    >
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-[#19b48d] flex items-center justify-center">
          <SafeIcon icon={FiCalculator} className="text-white text-lg" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Financial Calculators</h2>
          <p className="text-gray-600">Powerful tools to help you make informed financial decisions</p>
        </div>
      </div>

      {/* Calculator Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {calculatorCards.map((calculator, index) => {
          const colors = getColorClasses(calculator.color);
          return (
            <motion.div
              key={calculator.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`${colors.bg} rounded-xl shadow-sm border-2 ${colors.border} p-6 cursor-pointer transition-all duration-300 ${colors.hover} hover:shadow-lg flex flex-col h-full`}
              onClick={() => setActiveCalculator(calculator.id)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${colors.iconBg} flex items-center justify-center`}>
                  <SafeIcon icon={calculator.icon} className={`${colors.icon} text-2xl`} />
                </div>
                <div className="flex items-center space-x-2 text-sm text-gray-500">
                  <SafeIcon icon={FiCalculator} className="text-xs" />
                  <span>Calculator</span>
                </div>
              </div>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                {calculator.title}
              </h3>
              
              <p className="text-gray-600 text-sm mb-4 flex-grow">
                {calculator.description}
              </p>

              <div className="space-y-2 mt-auto">
                <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">Key Features:</p>
                <div className="flex flex-wrap gap-2">
                  {calculator.features.map((feature, idx) => (
                    <span 
                      key={idx} 
                      className="px-3 py-1 bg-white rounded-full text-xs text-gray-700 border border-gray-200"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <button 
                  className={`w-full flex items-center justify-center space-x-2 px-4 py-2 ${colors.iconBg} ${colors.icon} rounded-lg hover:opacity-80 transition-opacity duration-200 font-medium`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveCalculator(calculator.id);
                  }}
                >
                  <span>Open Calculator</span>
                  <SafeIcon icon={FiCalculator} className="text-sm" />
                </button>
              </div>
            </motion.div>
          );
        })}
      </div>

      {/* Info Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
        <div className="flex items-start space-x-3">
          <SafeIcon icon={FiPieChart} className="text-blue-600 text-lg mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 mb-2">How to Use These Calculators</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p>• <strong>Rent vs Buy:</strong> Find out if renting and investing the difference beats buying a flat over long periods.</p>
              <p>• <strong>Insurance vs MF:</strong> Compare your insurance policy returns with mutual fund alternatives.</p>
              <p>• <strong>SWP Calculator:</strong> Plan how much to invest now for future regular income needs.</p>
              <p>• <strong>Home Loan:</strong> Calculate EMI, compare prepayment vs SIP investment strategies.</p>
              <p>• <strong>Rental Yield:</strong> Analyze property investment returns vs lumpsum investment alternatives.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Modals */}
      <AnimatePresence>
        {activeCalculator === 'rent-vs-buy' && (
          <RentVsBuyCalculator onClose={() => setActiveCalculator(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeCalculator === 'insurance-vs-mf' && (
          <InsuranceVsMFCalculator onClose={() => setActiveCalculator(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeCalculator === 'swp' && (
          <SWPCalculator onClose={() => setActiveCalculator(null)} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeCalculator === 'home-loan' && (
          <HomeLoanCalculator onClose={() => setActiveCalculator(null)} onAddAsGoal={handleAddHomeLoanGoal} />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {activeCalculator === 'rental-yield' && (
          <RentalYieldCalculator onClose={() => setActiveCalculator(null)} />
        )}
      </AnimatePresence>

    </motion.div>
  );
}

export default Calculators;